<template>
  <div
    class="tw-w-full tw-flex-shrink-0 tw-cursor-pointer tw-rounded-[15px] tw-bg-white tw-p-[2px] tw-shadow-[0_2px_8px_rgba(0,0,0,0.15)] tw-transition-all tw-duration-200 tw-ease-in-out hover:tw--translate-y-0.5 hover:tw-shadow-lg"
    :class="{ 'tw-bg-gradient-orange': property.featured }"
    data-testid="property-card"
  >
    <div class="tw-w-full tw-rounded-[15px] tw-bg-white tw-p-[14px]">
      <!-- Property Card Content -->
      <div
        class="tw-flex tw-h-full tw-items-stretch tw-gap-[8px]"
        @click="openPropertyUrl"
      >
        <!-- Property Image -->
        <div
          class="tw-flex tw-max-h-[90px] tw-w-[30%] tw-flex-shrink-0 tw-overflow-hidden tw-rounded-lg"
        >
          <picture class="tw-h-full tw-w-full tw-object-cover">
            <source
              media="(max-width: 430px)"
              :srcset="
                getResponsiveImageUrls(firstImage)?.mobile ||
                cdnAsset('svg/no-image.svg')
              "
            />
            <source
              media="(max-width: 576px)"
              :srcset="
                getResponsiveImageUrls(firstImage)?.small ||
                cdnAsset('svg/no-image.svg')
              "
            />
            <source
              media="(max-width: 768px)"
              :srcset="
                getResponsiveImageUrls(firstImage)?.mobile ||
                cdnAsset('svg/no-image.svg')
              "
            />
            <source
              media="(max-width: 1024px)"
              :srcset="
                getResponsiveImageUrls(firstImage)?.tablet ||
                cdnAsset('svg/no-image.svg')
              "
            />
            <source
              media="(max-width: 1199px)"
              :srcset="
                getResponsiveImageUrls(firstImage)?.twoCol ||
                cdnAsset('svg/no-image.svg')
              "
            />
            <source
              media="(min-width: 1200px)"
              :srcset="
                getResponsiveImageUrls(firstImage)?.desktop ||
                cdnAsset('svg/no-image.svg')
              "
            />
            <img
              :alt="property.shortDescription"
              :src="
                getResponsiveImageUrls(firstImage)?.fallback ||
                cdnAsset('svg/no-image.svg')
              "
              decoding="async"
              loading="lazy"
              class="tw-h-full tw-w-full tw-object-cover"
            />
          </picture>
        </div>

        <!-- Property Details -->
        <div class="tw-min-w-0 tw-flex-1">
          <!-- Property Type with Action Buttons -->
          <div class="tw-flex tw-items-start tw-justify-between">
            <!-- Property Name -->
            <div class="tw-mb-[8px]">
              <div
                class="leading-[14px] tw-ellipsis tw-mb-[10px] tw-mr-2 tw-line-clamp-2 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-primary-grey"
                data-testid="property-name"
              >
                {{ property.name }}
              </div>

              <!-- Bed & Bath / Rooms Available -->
              <div
                v-if="isSpareRoom"
                class="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-primary-grey"
                data-testid="property-rooms-available"
              >
                <img
                  :src="cdnAsset('svg/uh-bed-icon.svg')"
                  class="tw-h-[18px] tw-w-[18px]"
                  alt="Rooms available"
                />
                <span class="tw-text-[14px]"
                  >{{ spareRoomsAvailableCount || 0 }}
                  {{ spareRoomsAvailableCount === 1 ? 'room' : 'rooms' }}
                  available
                </span>
              </div>
              <div
                v-else
                class="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-primary-grey"
              >
                <div
                  class="tw-flex tw-items-center tw-gap-0.5"
                  data-testid="property-rooms-count"
                >
                  <img
                    :src="cdnAsset('svg/uh-bed-icon.svg')"
                    class="tw-h-4 tw-w-4"
                    alt="Bedrooms"
                  />
                  <span>{{ property.roomsAvailableCount || '2' }}</span>
                </div>
                <div
                  class="tw-flex tw-items-center tw-gap-0.5"
                  data-testid="property-bathroom-count"
                >
                  <img
                    :src="cdnAsset('svg/bathtub-icon-square.svg')"
                    class="tw-h-4 tw-w-4"
                    alt="Bathrooms"
                  />
                  <span>{{ property.numberOfBathRooms || '1' }}</span>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="tw-flex tw-items-center tw-gap-2">
              <button
                type="button"
                :aria-label="
                  shortlisted
                    ? 'Remove from property shortlist'
                    : 'Add to property shortlist'
                "
                :data-shortlist="property?.id || ''"
                class="tw-flex tw-h-[40px] tw-w-[40px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-300 tw-bg-white tw-transition-colors hover:tw-bg-gray-50"
                @click.stop="toggleShortlist()"
                @touchstart.stop
                @touchend.stop
                @touchmove.stop
                @mouseover="focused = true"
                @mouseleave="focused = false"
                @blur="focused = false"
              >
                <img :src="iconAsset" alt="heart" class="tw-h-5 tw-w-5" />
              </button>
              <button
                type="button"
                data-testid="property-card-close"
                class="w-cursor-pointer tw-flex tw-h-[40px] tw-w-[40px] tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-300 tw-bg-white tw-transition-colors hover:tw-bg-gray-50"
                @click.stop="closeCard()"
                @touchstart.stop="closeCard()"
                @touchmove.stop
                @touchend.stop="closeCard()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M16.0372 14.7636C16.2063 14.9327 16.3012 15.162 16.3012 15.4011C16.3012 15.6402 16.2063 15.8695 16.0372 16.0386C15.8681 16.2077 15.6388 16.3026 15.3997 16.3026C15.1606 16.3026 14.9313 16.2077 14.7622 16.0386L10.0004 11.2753L5.23718 16.0371C5.06811 16.2062 4.83879 16.3011 4.59968 16.3011C4.36057 16.3011 4.13126 16.2062 3.96218 16.0371C3.79311 15.868 3.69812 15.6387 3.69812 15.3996C3.69812 15.1605 3.79311 14.9312 3.96218 14.7621L8.72543 10.0003L3.96368 5.23708C3.79461 5.068 3.69962 4.83869 3.69962 4.59958C3.69962 4.36047 3.79461 4.13115 3.96368 3.96208C4.13276 3.793 4.36207 3.69802 4.60118 3.69802C4.84029 3.69802 5.06961 3.793 5.23868 3.96208L10.0004 8.72533L14.7637 3.96133C14.9328 3.79225 15.1621 3.69727 15.4012 3.69727C15.6403 3.69727 15.8696 3.79225 16.0387 3.96133C16.2078 4.1304 16.3027 4.35972 16.3027 4.59883C16.3027 4.83794 16.2078 5.06725 16.0387 5.23633L11.2754 10.0003L16.0372 14.7636Z"
                    fill="#3B4143"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Price and Frequency -->
          <div
            class="tw-flex tw-items-baseline tw-gap-1"
            data-testid="property-price"
          >
            <div class="tw-text-lg tw-font-bold" style="color: #007eaf">
              £{{ Math.floor(property.price) }}
            </div>
            <div class="tw-text-xs tw-text-primary-grey">
              per week including bills
            </div>
          </div>
        </div>
      </div>

      <!-- Travel Times Section -->
      <div
        v-if="selectedUniversity && routeData && hasValidRouteData"
        class="tw-mt-4 tw-flex tw-items-center tw-justify-between tw-border-t tw-border-gray-200 tw-pt-4"
      >
        <!-- Walking -->
        <div
          v-if="routeData.walk && routeData.walk.duration"
          class="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-gray-500"
        >
          <img
            :src="cdnAsset('svg/phosphor/person-simple-walk-colored.svg')"
            class="tw-h-4 tw-w-4"
            alt="Walking"
          />
          <span>{{ formatTravelTime(routeData.walk.duration) }}</span>
        </div>

        <!-- Cycling -->
        <div
          v-if="routeData.bike && routeData.bike.duration"
          class="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-gray-500"
        >
          <img
            :src="cdnAsset('svg/phosphor/bicycle-colored.svg')"
            class="tw-h-4 tw-w-4"
            alt="Cycling"
          />
          <span>{{ formatTravelTime(routeData.bike.duration) }}</span>
        </div>

        <!-- Bus -->
        <div
          v-if="routeData.public && routeData.public.duration"
          class="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-gray-500"
        >
          <img
            :src="cdnAsset('svg/phosphor/bus-colored.svg')"
            class="tw-h-4 tw-w-4"
            alt="Bus"
          />
          <span>{{ formatTravelTime(routeData.public.duration) }}</span>
        </div>
      </div>
    </div>

    <SaveToShortlistModal
      v-if="
        enableHousemateGroups &&
        showHousemateGroupTab &&
        isLoggedInStudent &&
        property?.id
      "
      :open="showSaveToShortlistModal"
      :property-id="property.id"
      :is-logged-in-student="isLoggedInStudent"
      @close="showSaveToShortlistModal = false"
      @saved="handleSavedToDestination"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { useStudentStore } from '@js/stores/student/student';
import { cdnAsset } from '@js/vapor';
import { useFeedbackBar } from '@js/composables/useFeedbackBar';
import SaveToShortlistModal from '@components/Student/Modal/SaveToShortlistModal.vue';
import {
  showShortlistAddedNotification,
  showShortlistRemovedNotification,
} from '@components/Property/shortlistNotification';
import { sendLaunchDarklyMetric } from '@js/launchdarkly-track';

const createGuestStudentStore = () => ({
  housemateGroupShortlistedByPropertyId: {},
  isPersonallyShortlisted: () => false,
  async ensureHousemateGroupsShortlistContextLoaded() {},
  async toggleHousemateGroupsShortlist() {},
  async addToShortlist() {},
  async removeFromShortlist() {},
});
export default {
  name: 'PropertyCard',
  components: {
    SaveToShortlistModal,
  },
  inject: {
    shortlistNotificationsEnabled: {
      default: false,
    },
  },
  props: {
    enableHousemateGroups: {
      type: Boolean,
      default: false,
    },
    enableShortlistInSearch: {
      type: Boolean,
      default: false,
    },
    showHousemateGroupTab: {
      type: Boolean,
      default: true,
    },
    isLoggedInStudent: {
      type: Boolean,
      default: false,
    },
    property: {
      type: Object,
      default: null,
    },
    selectedUniversity: {
      type: Object,
      default: () => null,
    },
    routeData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      studentStore: this.isLoggedInStudent
        ? useStudentStore()
        : createGuestStudentStore(),
      isShortlisted: false,
      shortlistedHousemateGroupId: null,
      testImage: '',
      focused: false,
      feedbackBar: useFeedbackBar(),
      showSaveToShortlistModal: false,
    };
  },
  computed: {
    shortlisted() {
      return this.isShortlisted;
    },
    isSpareRoom() {
      return this.property?.advertType === 'spare_room';
    },
    spareRoomsAvailableCount() {
      return this.property?.spareRoomsAvailableCount ?? 0;
    },
    firstImage() {
      // Handle case where images might not exist
      if (!this.property.images) {
        return null;
      }

      // Handle case where images is already an object
      if (typeof this.property.images === 'object') {
        return Array.isArray(this.property.images) &&
          this.property.images.length > 0
          ? this.property.images[0]
          : null;
      }

      // Handle case where images is a JSON string
      if (typeof this.property.images === 'string') {
        try {
          const images = JSON.parse(this.property.images);
          return Array.isArray(images) && images.length > 0 ? images[0] : null;
        } catch (e) {
          console.error('Error parsing images JSON:', e);
          return null;
        }
      }

      return null;
    },
    label() {
      if (this.focused && this.shortlisted) return 'Remove';
      return this.shortlisted ? 'Shortlisted' : 'Shortlist';
    },
    iconAsset() {
      return this.shortlisted
        ? cdnAsset('svg/uh-shortlist-fill-icon.svg')
        : cdnAsset('svg/uh-shortlist-icon-outline.svg');
    },
    selectedUniversityTravelData() {
      return this.property.travelData.find(
        (data) => data.id === this.selectedUniversity.id
      );
    },
    hasValidRouteData() {
      return (
        this.routeData &&
        ((this.routeData.walk && this.routeData.walk.duration) ||
          (this.routeData.bike && this.routeData.bike.duration) ||
          (this.routeData.public && this.routeData.public.duration))
      );
    },
    getResponsiveImageUrls() {
      return (image) => {
        if (!image) return null;
        const baseUrl = image.fileURLTablet || image.mainImage;
        if (!baseUrl) return null;

        // Replace width parameter in Cloudflare CDN URL
        const extraSmallUrl = baseUrl
          .replace(/width=\d+/, 'width=120')
          .replace(/quality=75/, 'quality=90');
        const mobileUrl = baseUrl
          .replace(/width=\d+/, 'width=390')
          .replace(/quality=75/, 'quality=90');
        const smallUrl = baseUrl
          .replace(/width=\d+/, 'width=535')
          .replace(/quality=75/, 'quality=90');
        const tabletUrl = baseUrl
          .replace(/width=\d+/, 'width=468')
          .replace(/quality=75/, 'quality=90');
        const desktopUrl = baseUrl
          .replace(/width=\d+/, 'width=400')
          .replace(/quality=75/, 'quality=90');
        const twoColUrl = baseUrl
          .replace(/width=\d+/, 'width=548')
          .replace(/quality=75/, 'quality=90');

        return {
          mobile: mobileUrl,
          extraSmall: extraSmallUrl,
          small: smallUrl,
          tablet: tabletUrl,
          desktop: desktopUrl,
          twoCol: twoColUrl,
          fallback: image.mainImage,
        };
      };
    },
  },
  watch: {
    property: {
      handler(newProperty) {
        if (
          !this.enableHousemateGroups ||
          !this.showHousemateGroupTab ||
          !this.isLoggedInStudent
        ) {
          // Preserve legacy behavior when the flag is off.
          this.isShortlisted = Boolean(newProperty?.isShortlisted);
          this.shortlistedHousemateGroupId = null;
          return;
        }

        const propertyId = Number(newProperty?.id);
        const groupId = propertyId
          ? this.studentStore.housemateGroupShortlistedByPropertyId?.[
              String(propertyId)
            ]
          : null;

        if (groupId) {
          this.shortlistedHousemateGroupId = Number(groupId);
        }

        this.isShortlisted = Boolean(
          newProperty?.isShortlisted ||
            (propertyId
              ? this.studentStore.isPersonallyShortlisted?.(propertyId)
              : false) ||
            groupId
        );
      },
      immediate: true,
    },
  },
  created() {
    this.isShortlisted = Boolean(this.property?.isShortlisted);
  },
  mounted() {
    if (
      !this.enableHousemateGroups ||
      !this.showHousemateGroupTab ||
      !this.isLoggedInStudent
    ) {
      return;
    }

    (async () => {
      await this.studentStore
        .ensureHousemateGroupsShortlistContextLoaded?.()
        .catch(() => null);

      const propertyId = Number(this.property?.id);
      if (!propertyId) {
        return;
      }

      const groupId =
        this.studentStore.housemateGroupShortlistedByPropertyId?.[
          String(propertyId)
        ];

      if (groupId) {
        this.isShortlisted = true;
        this.shortlistedHousemateGroupId = Number(groupId);
        return;
      }

      // Fall back to personal shortlist / server-provided state.
      this.isShortlisted =
        this.studentStore.isPersonallyShortlisted?.(propertyId) ||
        Boolean(this.property?.isShortlisted);
    })();
  },
  methods: {
    closeCard() {
      this.$emit('close');
    },
    handleSavedToDestination({ destination, groupId } = {}) {
      const previousIsShortlisted = this.isShortlisted;
      this.isShortlisted = true;
      this.shortlistedHousemateGroupId =
        destination === 'group' ? groupId : null;
      this.$emit('shortlist-changed', {
        ...this.property,
        isShortlisted: previousIsShortlisted,
      });

      if (destination === 'group') {
        this.feedbackBar.show('Added to your grouplist');
      } else {
        this.showPersonalShortlistAddedFeedback();
      }
    },
    async addToShortlist() {
      if (!this.property?.id) return;
      this.sendLaunchDarklyEvent('map_shortlist');
      if (this.isLoggedInStudent) {
        return await this.studentStore.postAddToShortlist(
          this.property.id,
          this.property
        );
      }
      return await axios.post(`/student/shortlist/add/${this.property.id}`);
    },
    async removeFromShortlist() {
      if (!this.property?.id) return;
      if (this.isLoggedInStudent) {
        return await this.studentStore.removeFromShortlist(this.property.id);
      }
      return await axios.post(`/student/shortlist/remove/${this.property.id}`);
    },
    async toggleShortlist() {
      const previousIsShortlisted = this.isShortlisted;

      try {
        if (!this.property?.id) {
          return;
        }

        const propertyId = String(this.property.id || '').trim();
        if (!propertyId) {
          return;
        }

        if (
          this.enableHousemateGroups &&
          this.showHousemateGroupTab &&
          this.isLoggedInStudent
        ) {
          const result =
            await this.studentStore.toggleHousemateGroupsShortlist?.({
              propertyId,
              property: this.property,
              currentHousemateGroupId: this.shortlistedHousemateGroupId,
              isUiShortlisted: previousIsShortlisted,
            });

          if (result?.action === 'needs-modal') {
            this.showSaveToShortlistModal = true;
            return;
          }

          const nowShortlisted =
            result?.action === 'added-personal' ||
            result?.action === 'added-group';

          this.isShortlisted = nowShortlisted;
          this.shortlistedHousemateGroupId =
            result?.action === 'added-group' ? result?.groupId ?? null : null;

          this.$emit('shortlist-changed', {
            ...this.property,
            // Consumers expect the previous state and will toggle it.
            isShortlisted: previousIsShortlisted,
          });

          if (result?.action === 'removed-group') {
            this.feedbackBar.show(
              'Removed from your grouplist',
              'tw-bg-primary-grey'
            );
            return;
          }

          if (result?.action === 'removed-personal') {
            this.showPersonalShortlistRemovedFeedback();
            return;
          }

          if (result?.action === 'added-group') {
            this.feedbackBar.show('Added to your grouplist');
            return;
          }

          if (result?.action === 'added-personal') {
            this.showPersonalShortlistAddedFeedback();
            return;
          }

          return;
        }

        // Non-housemate-groups flow: optimistically toggle for map UI, then
        // persist via store.
        this.isShortlisted = !previousIsShortlisted;
        this.$emit('shortlist-changed', {
          ...this.property,
          isShortlisted: previousIsShortlisted,
        });

        if (!previousIsShortlisted) {
          await this.addToShortlist();
          this.showPersonalShortlistAddedFeedback();
          return;
        }

        await this.removeFromShortlist();

        this.showPersonalShortlistRemovedFeedback();
        return;
      } catch (err) {
        console.error('Error toggling shortlist', err);
        // Revert state on error (and notify map UI if we optimistically toggled).
        if (
          !this.enableHousemateGroups &&
          previousIsShortlisted !== this.isShortlisted
        ) {
          this.$emit('shortlist-changed', {
            ...this.property,
            isShortlisted: !previousIsShortlisted,
          });
        }
        this.isShortlisted = previousIsShortlisted;

        this.feedbackBar.show(
          'An error occurred, please try again later.',
          'error'
        );
      }
    },
    showPersonalShortlistAddedFeedback() {
      if (this.shortlistNotificationsEnabled) {
        showShortlistAddedNotification(this.property);
        return;
      }

      this.feedbackBar.show('Added to your shortlist');
    },
    showPersonalShortlistRemovedFeedback() {
      if (!this.shortlistNotificationsEnabled) {
        this.feedbackBar.show(
          'Removed from your shortlist',
          'tw-bg-primary-grey'
        );
        return;
      }

      showShortlistRemovedNotification(this.property, async () => {
        if (this.isShortlisted) return true;

        const propertyId = String(this.property?.id || '').trim();
        if (!propertyId) return false;

        const response =
          this.enableHousemateGroups &&
          this.showHousemateGroupTab &&
          this.isLoggedInStudent
            ? await this.studentStore.postAddToShortlist?.(
                propertyId,
                this.property
              )
            : await this.addToShortlist();

        if (!response?.data?.success) return false;

        const previousIsShortlisted = this.isShortlisted;
        this.isShortlisted = true;
        this.shortlistedHousemateGroupId = null;
        this.$emit('shortlist-changed', {
          ...this.property,
          isShortlisted: previousIsShortlisted,
        });
        return true;
      });
    },
    openPropertyUrl() {
      sendLaunchDarklyMetric('map_property_listing_viewed', {
        test_name: 'enable_map_libre_test',
      });
      window.open(this.property.url, '_blank');
    },
    formatTravelTime(durationInSeconds) {
      if (!durationInSeconds) return '';

      const totalMinutes = Math.round(durationInSeconds / 60);

      if (totalMinutes < 60) {
        return `${totalMinutes} mins`;
      }

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (minutes === 0) {
        return `${hours}${hours === 1 ? ' hour' : ' hours'}`;
      }

      return `${hours}${hours === 1 ? 'h' : 'h'} ${minutes}${
        minutes === 1 ? ' min' : ' mins'
      }`;
    },
    sendLaunchDarklyEvent(eventName, data = {}) {
      sendLaunchDarklyMetric(
        eventName,
        {
          ...data,
          test_name: 'enable_map_libre_test',
        },
        { transport: 'axios' }
      );
    },
  },
};
</script>

<style scoped>
/* Sticky property card styles */
.sticky-property-card {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}
</style>
